// janus-engine/ability/summon-caster-cantrips.ts — Wizard v9 (Geno, 2026-10-09), Dark Necromancy L30 "Death-Linked Familiar":
// `summons_gain_caster_cantrips` (int_0_1) — "Your summons gain access to your cantrips, and use your Bending and damage."
// FRONT END ONLY (owner ruling 2026-10-09: no PHP rule copy). The rule has two halves, both decided here:
//   1. ACCESS — a creature whose controller (encounter_creatures.controller_encounter_creature_id) provides the layer (root, ability
//      buckets, feats, active statuses — casterLayerProvider's walk) can cast each of the controller's CANTRIPS (spell_level 0 spells the
//      controller knows: its castable spells_known / at_will_spell lists). The browser lists inject them (summonInjectedCantrips) and the
//      cast-legitimacy gate grants them (cast-grant-gate.ts, path 'summon-cantrip').
//   2. STATS — the summon stays the caster (position, range, line of effect, AP, its own statuses / luck on the d20), but the cast's
//      stat reads come from the controller: the spell attack / contester skill (Bending + spellcasting essence), the counterspell
//      contest's Bending, and the cantrip's damage scaling (total_level). The page installs JanusReaders.summonCantripStatSource for the
//      one use it decided (summonCantripControllerFor); the engine reads it through statCreature() at those seams.
// A use the browser cannot resolve must never fall to the legacy PHP route (PHP would cast it with the summon's own stats): the page
// adds summonCantripLegacyMarker()'s row to its BROWSER-ONLY refusal check (browser-only-layers.ts), which names this layer.
import { type JanusCtx, type Row, ecId, handBack, record, upper } from './context.js';
import { casterLayerProvider } from '../actions/cast-range-gate.js';
import { type AbilityPreconditionReaders } from '../actions/ability-preconditions.js';
import { type SelectedSkill, rollerSkillBookkeeping, selectSkillForContestingRoll } from './select-skill-for-contesting-roll.js';

export const SUMMON_CANTRIPS_LAYER = 'summons_gain_caster_cantrips';

/** The minimal page / engine reader surface the rule needs (AbilityPreconditionReaders and the ability-use readers satisfy it). */
export interface SummonCantripReaders {
  value: (row: Row | null, key: string) => unknown;
  entries: (row: Row | null, key: string) => unknown[];
  statuses: (row: Row) => Row[];
  /** A catalog id (or a list entry) → its full definition; null when the page does not hold it. */
  resolveItem: (entry: unknown) => Row | null;
}

const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
const scalar = (v: unknown): unknown => (Array.isArray(v) ? v[0] : v);

/** The controller a row acts for (its summoner), '' when it is not a controlled creature. */
export function summonControllerId(summon: Row | null | undefined): string {
  const id = upper(scalar(record(summon?.encounter_creatures)?.controller_encounter_creature_id));
  return UUID.test(id) ? id : '';
}

/** The ids one list entry names (a bare "uuid|…" string, {item_id}, {items:{item_id}}, {nest}/{item_arr} definitions). */
function entryId(entry: unknown): string {
  if (typeof entry === 'string') return upper(entry.split('|')[0]);
  const row = record(entry);
  if (!row) return '';
  for (const raw of [row.item_id, row.id, record(row.items)?.item_id, record(row.nest)?.item_id, record(record(row.nest)?.items)?.item_id,
    record(row.item_arr)?.item_id, record(record(row.item_arr)?.items)?.item_id, typeof row.nest === 'string' ? row.nest : null]) {
    const id = upper(typeof scalar(raw) === 'string' ? String(scalar(raw)).split('|')[0] : '');
    if (UUID.test(id)) return id;
  }
  return '';
}
/** The entry's own full definition (nest / item_arr / itself carrying layers), else the reader's. */
function definitionOf(read: SummonCantripReaders, entry: unknown, id: string): Row | null {
  const row = record(entry);
  for (const candidate of [record(row?.nest), record(row?.item_arr), row]) if (candidate && record(candidate.item_layer_types)) return candidate;
  return read.resolveItem(id);
}

/** The castable lists a creature's spells sit in: the baked castable payload, the compiled creature lists, the layer buckets. */
function spellLists(read: SummonCantripReaders, creature: Row, castableKeys: readonly string[], layerKeys: readonly string[]): unknown[][] {
  const compiled = record(record(creature.computed)?.creature);
  const castable = record(compiled?.castable);
  const lists: unknown[][] = [];
  for (const key of castableKeys) if (Array.isArray(castable?.[key])) lists.push(castable![key] as unknown[]);
  for (const key of ['spells_known'].filter(k => castableKeys.includes(k))) if (Array.isArray(compiled?.[key])) lists.push(compiled![key] as unknown[]);
  for (const key of layerKeys) lists.push(read.entries(creature, key));
  return lists;
}

/** Does `controller` provide the layer? true / false, or null when an entry the page cannot resolve might be the provider. */
export function controllerGrantsSummonCantrips(read: SummonCantripReaders, controller: Row): boolean | null {
  const preconditionRead = {
    ...read,
    has: (row: Row | null, key: string) => !!row && (read.value(row, key) != null || read.entries(row, key).length > 0),
    inCombat: () => false,
  } as unknown as AbilityPreconditionReaders;
  const provider = casterLayerProvider(preconditionRead, controller, SUMMON_CANTRIPS_LAYER);
  return provider === undefined ? null : provider !== null;
}

export interface ControllerCantrip { item_id: string; definition: Row }
/** The controller's CANTRIPS: the spells it knows or casts at will whose spell_level is 0 (the castable bake's `(int)(spell_level ?? 0)`).
 *  Only a held SPELL definition qualifies (a curse, an unresolvable id or a leveled spell never does); first occurrence wins. */
export function controllerCantrips(read: SummonCantripReaders, controller: Row): ControllerCantrip[] {
  const out: ControllerCantrip[] = [];
  const seen = new Set<string>();
  for (const list of spellLists(read, controller, ['spells_known', 'at_will_spell'], ['spells_known', 'at_will_spell'])) {
    for (const entry of list) {
      const id = entryId(entry);
      if (!UUID.test(id) || seen.has(id)) continue;
      const definition = definitionOf(read, entry, id);
      if (!definition) continue;
      const items = record(definition.items);
      if (String(scalar(items?.item_type ?? definition.item_type) ?? '') !== 'spell') continue;
      const baked = record(entry)?.level;
      const level = Number(scalar(baked ?? read.value(definition, 'spell_level') ?? 0));
      if (level !== 0) continue;
      seen.add(id);
      out.push({ item_id: id, definition });
    }
  }
  return out;
}

/** Does the summon cast this spell from a grant of its OWN (then it is its own cast, with its own stats)? */
export function summonHoldsSpell(read: SummonCantripReaders, summon: Row, spellId: string): boolean {
  const want = upper(spellId);
  const lists = spellLists(read, summon,
    ['spells_known', 'curses_known', 'at_will_spell', 'innate_spell_per_long_rest', 'charged_spell'],
    ['spells_known', 'at_will_spell', 'innate_spell_per_long_rest', 'charged_spell',
      'spell_prepared_1', 'spell_prepared_2', 'spell_prepared_3']);
  return lists.some(list => list.some(entry => entryId(entry) === want));
}

/** One injected castable row: a plain native-shape spell row (no spell_type / source marker — a cantrip costs no mana and rides no grant
 *  variable), stamped with the controller it comes from so a list / the cast lane can tell it apart. */
export interface SummonCantripEntry { item_id: string; level: 0; nest: Row; summon_cantrip_controller_id: string; cast_with_controller_stats: true }
/** The controller's cantrips the summon gains (none unless the controller provably provides the layer); the summon's own spells excluded. */
export function summonInjectedCantrips(read: SummonCantripReaders, summon: Row, controller: Row | null | undefined): SummonCantripEntry[] {
  const controllerId = summonControllerId(summon);
  if (!controller || !controllerId || ecId(controller) !== controllerId) return [];
  if (controllerGrantsSummonCantrips(read, controller) !== true) return [];
  return controllerCantrips(read, controller).filter(c => !summonHoldsSpell(read, summon, c.item_id))
    .map(c => ({ item_id: c.item_id, level: 0, nest: c.definition, summon_cantrip_controller_id: controllerId, cast_with_controller_stats: true }));
}

/** The cast lane's decision for ONE use: the controller id whose stats the summon casts `abilityId` with, or null (an ordinary cast). */
export function summonCantripControllerFor(read: SummonCantripReaders, summon: Row, controller: Row | null | undefined, abilityId: string): string | null {
  const want = upper(abilityId);
  return summonInjectedCantrips(read, summon, controller).some(c => c.item_id === want) ? summonControllerId(summon) : null;
}

/** Is this use a CANDIDATE (the summon has a controller, the ability is a level-0 spell, the summon does not hold it)? Decided on the
 *  summon and the definition alone, so the page knows to hydrate the controller before it decides. */
export function summonCantripCandidate(read: SummonCantripReaders, summon: Row, ability: Row | null | undefined): boolean {
  if (!ability || !summonControllerId(summon)) return false;
  if (String(scalar(record(ability.items)?.item_type ?? ability.item_type) ?? '') !== 'spell') return false;
  if (Number(scalar(read.value(ability, 'spell_level') ?? 0)) !== 0) return false;
  return !summonHoldsSpell(read, summon, entryId(ability));
}

/** The legacy-route marker: a row carrying the layer when this use is (or may be — the controller is not held / its grant is unprovable)
 *  an injected cantrip. Pass it with the ability and caster rows to browserOnlyLegacyRouteReason(). null = an ordinary use. */
export function summonCantripLegacyMarker(read: SummonCantripReaders, summon: Row | null | undefined, controller: Row | null | undefined,
  ability: Row | null | undefined): Row | null {
  if (!summon || !summonCantripCandidate(read, summon, ability)) return null;
  const grants = controller && ecId(controller) === summonControllerId(summon) ? controllerGrantsSummonCantrips(read, controller) : null;
  if (grants === false) return null;
  if (grants === true && !controllerCantrips(read, controller!).some(c => c.item_id === entryId(ability))) return null;
  return { items: { item_type: 'summon_cantrip_marker' }, item_layer_types: { [SUMMON_CANTRIPS_LAYER]: 1 } };
}

/**
 * The creature whose STATS a roll of this use reads: the controller for an injected cantrip the page decided (JanusReaders
 * .summonCantripStatSource), else the caster itself. Position, AP, statuses and luck stay the caster's — only the skill selection
 * (Bending tier + spellcasting essence) and the cantrip's damage scaling read this row.
 */
export function statCreature(ctx: JanusCtx, caster: Row, abilityId: unknown): Row {
  const source = ctx.read.summonCantripStatSource;
  if (!source) return caster;
  const controllerId = source(ecId(caster), upper(abilityId));
  if (!controllerId) return caster;
  const controller = ctx.members.get(upper(controllerId));
  if (!controller) handBack('summons_gain_caster_cantrips:controller-not-held');
  return controller;
}

/**
 * selectSkillForContestingRoll for the CASTER's side of this use's roll. An ordinary cast: unchanged. An injected cantrip: the skill's
 * NUMBERS — which skill, its tier, the spellcasting essence, its mods, a compiled forced natural — are the controller's ("use your Bending");
 * its ADV / DIS bookkeeping stays the summon's own (the roller's held statuses / compiled breakdown — skillRollArr's guardRollLayers vets the
 * roller's carriers against exactly these sources, so a summon's Exhausted is counted, never dropped; Codex review 2026-10-09). The
 * untrained-skill DIS follows the controller's tier, not the summon's.
 */
export function selectCastingSkill(ctx: JanusCtx, caster: Row, abilityId: unknown, entries: unknown[]): SelectedSkill {
  const source = statCreature(ctx, caster, abilityId);
  if (source === caster) return selectSkillForContestingRoll(ctx, caster, entries);
  const stat = selectSkillForContestingRoll(ctx, source, entries);
  const own = rollerSkillBookkeeping(ctx, caster, stat.id);
  const untrained = (tier: number): number => (tier === 0 ? 1 : 0);
  return { ...stat, adv: own.adv + untrained(own.tier) - untrained(stat.tier), compiledAdvKnown: own.compiledAdvKnown,
    compiledAdvSources: own.compiledAdvSources, compiledAdvSourceValues: own.compiledAdvSourceValues, advDisTrace: own.advDisTrace };
}

/** The page's decision for one candidate use, once the controller row is held: `controller` = roll its stats; `ordinary` = the cast is
 *  not this rule's (no grant, not a controller cantrip); `unproved` = the page cannot tell (controller not held, an unresolvable grant
 *  carrier) — the lane must hand back pre-draw rather than resolve with the summon's own stats. */
export function summonCantripDecision(read: SummonCantripReaders, summon: Row, controller: Row | null | undefined, abilityId: string):
  { kind: 'controller'; id: string } | { kind: 'ordinary' } | { kind: 'unproved' } {
  const controllerId = summonControllerId(summon);
  if (!controllerId) return { kind: 'ordinary' };
  if (!controller || ecId(controller) !== controllerId) return { kind: 'unproved' };
  const grants = controllerGrantsSummonCantrips(read, controller);
  if (grants === null) return { kind: 'unproved' };
  if (!grants) return { kind: 'ordinary' };
  return summonCantripControllerFor(read, summon, controller, abilityId) ? { kind: 'controller', id: controllerId } : { kind: 'ordinary' };
}
